import { useEffect, useRef, useState } from "react";
import { useLocation } from "wouter";
import { useTranslation } from "react-i18next";
import { Sparkles, Activity, LayoutDashboard, MessageSquare, X } from "lucide-react";
import { useRecentLogs } from "@/hooks/use-recent-logs";
import { useGetCurrentUser } from "@workspace/api-client-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import {
  getFirstLogCelebrated,
  markFirstLogCelebrated,
  getKnownEmptyLogState,
  setKnownEmptyLogState,
} from "@/hooks/use-activation-status";

export function FirstLogCelebration() {
  const { t } = useTranslation();
  const [, setLocation] = useLocation();
  const { data: user } = useGetCurrentUser();
  const { data: recent } = useRecentLogs(1);
  const [open, setOpen] = useState(false);
  const fired = useRef(false);

  useEffect(() => {
    if (fired.current) return;
    if (!user || !recent) return;
    const uid = (user as any)?.email ?? null;
    if (getFirstLogCelebrated(uid)) { fired.current = true; return; }
    const wasEmpty = getKnownEmptyLogState(uid);
    if (recent.length === 0) {
      // Record that we observed empty state; fire only on the empty→non-empty transition
      setKnownEmptyLogState(uid, true);
      return;
    }
    // recent.length > 0
    if (wasEmpty) {
      fired.current = true;
      markFirstLogCelebrated(uid);
      setKnownEmptyLogState(uid, false);
      setOpen(true);
    } else {
      // Pre-existing user with logs and no record — don't surprise them; just mark celebrated.
      fired.current = true;
      markFirstLogCelebrated(uid);
      setKnownEmptyLogState(uid, false);
    }
  }, [user, recent]);

  const close = () => setOpen(false);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent
        className="max-w-md border border-primary/30 bg-card"
        data-testid="first-log-celebration"
      >
        <button
          type="button"
          onClick={close}
          className="absolute right-3 top-3 text-muted-foreground hover:text-foreground"
          aria-label={t("common.close")}
        >
          <X className="w-4 h-4" />
        </button>
        <div className="flex flex-col items-center text-center pt-2">
          <div className="w-14 h-14 rounded-full bg-primary/15 border border-primary/30 flex items-center justify-center mb-3 shadow-[0_0_24px_-4px_hsl(var(--primary)/0.6)]">
            <Sparkles className="w-7 h-7 text-primary" />
          </div>
          <h2 className="text-lg font-bold tracking-tight mb-1">
            {t("firstLogCelebration.title")}
          </h2>
          <p className="text-sm text-muted-foreground mb-5 max-w-sm">
            {t("firstLogCelebration.description")}
          </p>
          <div className="w-full grid grid-cols-1 gap-2 mb-5 text-left">
            <UnlockRow icon={Activity} title={t("firstLogCelebration.unlockNrsTitle")} desc={t("firstLogCelebration.unlockNrsDesc")} />
            <UnlockRow icon={LayoutDashboard} title={t("firstLogCelebration.unlockDashboardTitle")} desc={t("firstLogCelebration.unlockDashboardDesc")} />
            <UnlockRow icon={MessageSquare} title={t("firstLogCelebration.unlockDeepCommandTitle")} desc={t("firstLogCelebration.unlockDeepCommandDesc")} />
          </div>
          <div className="flex items-center gap-2 w-full">
            <Button variant="outline" size="sm" className="flex-1" onClick={close} data-testid="first-log-celebration-dismiss">
              {t("firstLogCelebration.keepLogging")}
            </Button>
            <Button size="sm" className="flex-1 gap-1.5" onClick={() => { close(); setLocation("/dashboard"); }} data-testid="first-log-celebration-cta">
              <LayoutDashboard className="w-4 h-4" />
              {t("firstLogCelebration.viewDashboard")}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function UnlockRow({ icon: Icon, title, desc }: { icon: any; title: string; desc: string }) {
  return (
    <div className="flex items-start gap-2.5 rounded-lg border border-border/40 bg-muted/15 p-2.5">
      <div className="w-7 h-7 rounded-md bg-primary/10 border border-primary/20 flex items-center justify-center shrink-0 mt-0.5">
        <Icon className="w-3.5 h-3.5 text-primary" />
      </div>
      <div className="min-w-0">
        <div className="text-xs font-semibold">{title}</div>
        <div className="text-[11px] text-muted-foreground leading-snug">{desc}</div>
      </div>
    </div>
  );
}
